import { useState, useEffect, useRef } from 'react';

export default function OTPVerification() {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(600); // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);

  // Timer countdown
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => {
          const newTime = prev - 1;
          if (newTime <= 540) { // Enable resend after 60 seconds
            setCanResend(true);
          }
          return newTime;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Format timer as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return; // Only allow single digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(''); // Clear errors when user types

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (newOtp.every(digit => digit !== '') && value) {
      handleVerification(newOtp.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.match(/\d/g);
    
    if (digits && digits.length >= 6) {
      const newOtp = digits.slice(0, 6);
      setOtp(newOtp);
      
      // Focus last input
      inputRefs.current[5]?.focus();
      
      // Auto-submit
      handleVerification(newOtp.join(''));
    }
  };

  // Verify OTP
  const handleVerification = async (code = otp.join('')) => {
    if (code.length !== 6) return;
    if (attempts >= 5) {
      setError('Maximum attempts exceeded. Please request a new code.');
      return;
    }

    // Prevent duplicate submissions
    if (isVerifying) {
      console.log('Verification already in progress, ignoring duplicate call');
      return;
    }

    setIsVerifying(true);
    setError(''); // Clear any previous errors

    try {
      const registrationData = JSON.parse(sessionStorage.getItem('registrationData') || '{}');

      if (!registrationData.email || !registrationData.sessionToken) {
        setError('Registration data not found. Please register again.');
        window.location.assign('/register');
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          email: registrationData.email,
          sessionToken: registrationData.sessionToken
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Verification failed' }));
        throw new Error(errorData.detail || 'Verification failed');
      }

      const data = await response.json();

      if (data.verified) {
        // Success! Store auth data and redirect to dashboard
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
        }

        // Store user data
        if (data.user) {
          localStorage.setItem('user_data', JSON.stringify(data.user));
        }

        sessionStorage.removeItem('registrationData');

        // Show success message before redirect
        setError('');
        window.location.assign('/customer-dashboard');
      } else {
        throw new Error(data.detail || 'Invalid verification code');
      }
    } catch (error) {
      console.error('OTP verification error:', error);

      if (error.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(error.message || 'Network error. Please try again.');
      }

      setAttempts(prev => prev + 1);

      // Only clear OTP if we haven't exceeded max attempts
      if (attempts < 4) {
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    try {
      const registrationData = JSON.parse(sessionStorage.getItem('registrationData') || '{}');
      
      const response = await fetch('/api/auth/resend-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registrationData.email })
      });

      if (response.ok) {
        setTimer(600); // Reset timer
        setCanResend(false);
        setAttempts(0); // Reset attempts
        setError('');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to resend code');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Verify your email
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            We sent a 6-digit code to your email address
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {/* OTP Input Fields */}
          <div className="flex justify-center space-x-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                data-testid={`otp-input-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={attempts >= 5}
                className={`w-12 h-12 text-center text-xl font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  attempts >= 5 
                    ? 'bg-gray-100 border-gray-300' 
                    : digit 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-600 text-center">
              {error}
              {attempts > 0 && attempts < 5 && (
                <div className="text-xs mt-1">
                  {5 - attempts} attempts remaining
                </div>
              )}
            </div>
          )}

          {/* Timer and Resend */}
          <div className="text-center space-y-3">
            <div 
              data-testid="timer"
              className={`text-sm font-mono ${timer < 60 ? 'text-red-600' : 'text-gray-600'}`}
            >
              Time remaining: {formatTime(timer)}
            </div>
            
            <button
              data-testid="resend-btn"
              onClick={handleResend}
              disabled={!canResend || timer <= 0}
              className="text-sm text-blue-600 hover:text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {!canResend && timer > 540 ? 'Resend available in 60s' : 'Resend code'}
            </button>
          </div>

          {/* Loading State */}
          {isVerifying && (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Manual Verify Button (if needed) */}
          <button
            onClick={() => handleVerification()}
            disabled={otp.some(digit => !digit) || isVerifying || attempts >= 5}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Verify Code
          </button>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Didn't receive the code?{' '}
              <button
                onClick={handleResend}
                disabled={!canResend}
                className="font-medium text-blue-600 hover:text-blue-500 disabled:text-gray-400"
              >
                Contact support
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}